/*
 * Clinch IT — <clinch-product-gallery>
 * Vertical thumbnail rail synced to a main Swiper, plus a lightbox Swiper
 * opened by clicking the main image. Also reacts to variant changes so the
 * gallery jumps to the selected variant's featured media.
 */
(function () {
  class ClinchProductGallery extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.lightboxEl = this.querySelector('[data-cpg-lightbox]');

      if (window.Swiper) {
        this.init();
      } else {
        window.addEventListener('load', () => this.init(), { once: true });
      }
    }

    init() {
      if (!window.Swiper || this.swiper) return;

      const thumbsEl = this.querySelector('[data-cpg-thumbs]');
      const mainEl = this.querySelector('[data-cpg-main]');

      // Stacked / columns layouts have no rail and no main slider — the media
      // is laid out in the markup and only the lightbox is interactive.
      if (!mainEl) {
        this.bindLightbox();
        return;
      }

      if (thumbsEl) {
        // 'vertical' = rail beside the image, 'horizontal' = strip below it.
        // Below 990px both collapse to a horizontal strip.
        const isVertical = this.dataset.thumbDirection !== 'horizontal';
        const perView = isVertical ? 5 : 6;

        this.thumbs = new window.Swiper(thumbsEl, {
          direction: isVertical ? 'vertical' : 'horizontal',
          slidesPerView: perView,
          spaceBetween: 10,
          watchSlidesProgress: true,
          freeMode: true,
          // Scroll the rail when there are more thumbs than fit.
          mousewheel: { forceToAxis: true, releaseOnEdges: true },
          scrollbar: { el: thumbsEl.querySelector('.swiper-scrollbar'), draggable: true, hide: false },
          breakpoints: {
            0: { direction: 'horizontal', slidesPerView: 4, spaceBetween: 8 },
            990: { direction: isVertical ? 'vertical' : 'horizontal', slidesPerView: perView, spaceBetween: 10 },
          },
        });

        // Only show the scrollbar when the rail actually overflows.
        const slideCount = thumbsEl.querySelectorAll('.swiper-slide').length;
        thumbsEl.classList.toggle('cpg__thumbs--scrollable', slideCount > perView);
      }

      this.swiper = new window.Swiper(mainEl, {
        slidesPerView: 1,
        spaceBetween: 0,
        navigation: {
          nextEl: mainEl.querySelector('.swiper-button-next'),
          prevEl: mainEl.querySelector('.swiper-button-prev'),
        },
        thumbs: this.thumbs ? { swiper: this.thumbs } : undefined,
      });

      this.bindLightbox();
    }

    bindLightbox() {
      const swiperEl = this.querySelector('[data-cpg-lightbox-swiper]');
      if (!this.lightboxEl || !swiperEl) return;

      // Move the lightbox to <body>. Inside the product card its fixed
      // positioning is trapped by ancestor stacking contexts (sticky column,
      // transformed swiper wrappers) so the backdrop never covers the page.
      document.body.appendChild(this.lightboxEl);

      this.addEventListener('click', (event) => {
        const zoom = event.target.closest('.cpg__zoom');
        if (zoom) {
          event.preventDefault();
          // Stacked items carry their own index; slider mode uses the active slide.
          this.openLightbox(zoom.dataset.index ? Number(zoom.dataset.index) : undefined);
        }
      });

      this.lightboxEl.addEventListener('click', (event) => {
        if (event.target.closest('[data-cpg-close]')) this.closeLightbox();
      });

      this.onKeydown = (event) => {
        if (this.lightboxEl.hidden) return;
        if (event.key === 'Escape') this.closeLightbox();
      };
      document.addEventListener('keydown', this.onKeydown);
    }

    openLightbox(startIndex) {
      const swiperEl = this.lightboxEl.querySelector('[data-cpg-lightbox-swiper]');
      let index = 0;
      if (typeof startIndex === 'number' && !Number.isNaN(startIndex)) {
        index = startIndex;
      } else if (this.swiper) {
        index = this.swiper.activeIndex;
      }

      this.lightboxEl.hidden = false;
      document.body.classList.add('overflow-hidden', 'cpg-lightbox-open');

      // Swap in the full-size sources on first open.
      this.lightboxEl.querySelectorAll('img[data-src]').forEach((img) => {
        img.src = img.dataset.src;
        img.removeAttribute('data-src');
      });

      if (!this.lightboxSwiper) {
        this.lightboxSwiper = new window.Swiper(swiperEl, {
          slidesPerView: 1,
          spaceBetween: 0,
          initialSlide: index,
          keyboard: { enabled: true },
          navigation: {
            nextEl: swiperEl.querySelector('.swiper-button-next'),
            prevEl: swiperEl.querySelector('.swiper-button-prev'),
          },
          pagination: { el: swiperEl.querySelector('.swiper-pagination'), clickable: true },
        });
      } else {
        this.lightboxSwiper.update();
        this.lightboxSwiper.slideTo(index, 0);
      }

      // Swiper measures on init; re-measure once layout has settled after the
      // overlay became visible.
      requestAnimationFrame(() => this.lightboxSwiper && this.lightboxSwiper.update());

      const closeButton = this.lightboxEl.querySelector('.cpg-lightbox__close');
      if (closeButton) closeButton.focus();
    }

    closeLightbox() {
      if (!this.lightboxEl || this.lightboxEl.hidden) return;

      // Keep the main slider in sync with wherever the lightbox ended up.
      if (this.lightboxSwiper && this.swiper) {
        this.swiper.slideTo(this.lightboxSwiper.activeIndex);
      }

      this.lightboxEl.hidden = true;
      document.body.classList.remove('overflow-hidden', 'cpg-lightbox-open');
    }

    /** Public: jump the gallery to a media id. Called from product-info.js on
     *  variant change; accepts a raw id or a "sectionId-mediaId" string. */
    setActiveMedia(mediaId) {
      if (!this.swiper) return;
      const id = String(mediaId).split('-').pop();
      const slides = Array.from(this.querySelectorAll('.cpg__main-slide'));
      const index = slides.findIndex((slide) => slide.dataset.mediaId === id);
      if (index >= 0) this.swiper.slideTo(index);
    }

    disconnectedCallback() {
      if (this.onKeydown) document.removeEventListener('keydown', this.onKeydown);
      document.body.classList.remove('overflow-hidden', 'cpg-lightbox-open');
      // The lightbox was portalled to <body>, so it won't be removed with us.
      if (this.lightboxEl && this.lightboxEl.parentElement === document.body) {
        this.lightboxEl.remove();
      }
      [this.lightboxSwiper, this.swiper, this.thumbs].forEach((instance) => {
        if (instance) instance.destroy(true, true);
      });
      this.lightboxSwiper = this.swiper = this.thumbs = null;
      this.initialized = false;
    }
  }

  if (!customElements.get('clinch-product-gallery')) {
    customElements.define('clinch-product-gallery', ClinchProductGallery);
  }
})();
